import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Plus, Trash2, BookOpen, GripVertical, Users, Stethoscope, Pencil, Check, X, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fetchUserCptCodes, addUserCptCode, updateUserCptCode, deleteUserCptCode, reorderUserCptCodes } from "@/lib/api";
import { CPT_LOOKUP } from "@/lib/cptLookup";
import { useTheme } from "@/contexts/ThemeContext";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Context Menu Component
function ContextMenu({ x, y, entry, onClose, onAddToMisc, onAddToProcedure, onEdit }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  useEffect(() => {
    const handleClick = () => onClose();
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  return (
    <div 
      className={`fixed z-[200] border rounded-lg shadow-xl py-1 min-w-[200px] ${
        isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'
      }`}
      style={{ left: x, top: y }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={`px-3 py-2 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
        <p className={`text-xs font-mono font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{entry.code}</p>
        <p className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{entry.description}</p>
      </div>
      <button
        onClick={() => { onAddToMisc(entry); onClose(); }}
        className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors text-left ${
          isDark 
            ? 'text-slate-200 hover:bg-purple-500/20 hover:text-purple-300'
            : 'text-gray-700 hover:bg-purple-50 hover:text-purple-600'
        }`}
      >
        <Users size={14} className={isDark ? 'text-purple-400' : 'text-purple-600'} />
        Add to Other Patient Visits
      </button>
      <button
        onClick={() => { onAddToProcedure(entry); onClose(); }}
        className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors text-left ${
          isDark 
            ? 'text-slate-200 hover:bg-teal-500/20 hover:text-teal-300'
            : 'text-gray-700 hover:bg-teal-50 hover:text-teal-600'
        }`}
      >
        <Stethoscope size={14} className={isDark ? 'text-teal-400' : 'text-teal-600'} />
        Add to Procedures & Services
      </button>
      <div className={`border-t my-1 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}></div>
      <button
        onClick={() => { onEdit(entry); onClose(); }}
        className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors text-left ${
          isDark 
            ? 'text-slate-200 hover:bg-amber-500/20 hover:text-amber-300'
            : 'text-gray-700 hover:bg-amber-50 hover:text-amber-600'
        }`}
      >
        <Pencil size={14} className={isDark ? 'text-amber-400' : 'text-amber-600'} />
        Edit
      </button>
    </div>
  );
}

// Sortable item component with inline editing
function SortableItem({ entry, onRemove, onContextMenu, isEditing, onEdit, onSaveEdit, onCancelEdit, editForm, onEditFormChange }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  // Long-press handling for mobile
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [touchStartPos, setTouchStartPos] = useState(null);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : "auto",
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    onContextMenu(e, entry);
  };

  // Touch handlers for long-press on mobile
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setTouchStartPos({ x: touch.clientX, y: touch.clientY });
    
    const timer = setTimeout(() => {
      // Trigger haptic feedback if available
      if (navigator.vibrate) navigator.vibrate(50);
      
      // Create a synthetic event for the context menu
      onContextMenu({ 
        preventDefault: () => {},
        clientX: touch.clientX, 
        clientY: touch.clientY 
      }, entry);
    }, 500); // 500ms long-press
    
    setLongPressTimer(timer);
  };

  const handleTouchMove = (e) => {
    if (!touchStartPos) return;
    
    const touch = e.touches[0];
    const moveThreshold = 10; // pixels
    const dx = Math.abs(touch.clientX - touchStartPos.x);
    const dy = Math.abs(touch.clientY - touchStartPos.y);
    
    // If moved too much, cancel long-press (user is scrolling/dragging)
    if (dx > moveThreshold || dy > moveThreshold) {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        setLongPressTimer(null);
      }
    }
  };

  const handleTouchEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    setTouchStartPos(null);
  };

  // If this entry is being edited, show edit form
  if (isEditing) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`relative p-2 rounded-lg border ring-1 ${
          isDark 
            ? 'bg-slate-800/60 border-amber-500/50 ring-amber-500/30'
            : 'bg-amber-50 border-amber-300 ring-amber-200'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Pencil size={12} className={isDark ? 'text-amber-400' : 'text-amber-600'} />
            <Input
              placeholder="Code"
              value={editForm.code}
              onChange={(e) => onEditFormChange({ ...editForm, code: e.target.value })}
              className={`bg-transparent border-none text-sm font-mono font-bold h-6 w-14 p-0 focus-visible:ring-0 ${
                isDark ? 'text-teal-400 placeholder:text-teal-400/40' : 'text-teal-700 placeholder:text-teal-700/40'
              }`}
              maxLength={5}
              autoFocus
            />
          </div>
          <Input
            type="number"
            step="0.01"
            placeholder="wRVU"
            value={editForm.wrvu}
            onChange={(e) => onEditFormChange({ ...editForm, wrvu: e.target.value })}
            className={`bg-transparent border-none text-sm font-mono h-6 w-12 p-0 text-right focus-visible:ring-0 ${
              isDark ? 'text-cyan-400 placeholder:text-cyan-400/40' : 'text-cyan-700 placeholder:text-cyan-700/40'
            }`}
          />
        </div>
        <Input
          placeholder="Description"
          value={editForm.description}
          onChange={(e) => onEditFormChange({ ...editForm, description: e.target.value })}
          className={`bg-transparent border-none text-xs h-5 mt-1 ml-6 p-0 focus-visible:ring-0 ${
            isDark ? 'text-slate-400 placeholder:text-slate-500' : 'text-gray-700 placeholder:text-gray-400'
          }`}
        />
        <div className={`flex items-center gap-2 mt-2 pt-2 border-t ${isDark ? 'border-slate-700/50' : 'border-gray-200'}`}>
          <Button
            onClick={onSaveEdit}
            disabled={!editForm.code.trim() || !editForm.description.trim()}
            size="sm"
            className="flex-1 bg-amber-600 hover:bg-amber-500 text-white text-xs h-6"
          >
            Save
          </Button>
          <Button
            onClick={onCancelEdit}
            size="sm"
            variant="ghost"
            className={`text-xs h-6 px-2 ${
              isDark ? 'text-slate-500 hover:text-slate-300' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // Normal display mode
  return (
    <div
      ref={setNodeRef}
      style={style}
      onContextMenu={handleContextMenu}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      className={`relative p-2 rounded-lg border group transition-all cursor-context-menu ${
        isDark 
          ? `bg-slate-800/60 border-slate-700/50 hover:bg-teal-500/10 hover:border-teal-500/30 ${isDragging ? "shadow-lg ring-2 ring-teal-500/50 bg-teal-500/20" : ""}`
          : `bg-white border-gray-200 hover:bg-teal-50 hover:border-teal-300 ${isDragging ? "shadow-lg ring-2 ring-teal-300 bg-teal-50" : ""}`
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Drag handle */}
          <button
            {...attributes}
            {...listeners}
            className={`cursor-grab active:cursor-grabbing touch-none ${
              isDark ? 'text-slate-500 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'
            }`}
            title="Drag to reorder"
          >
            <span className="text-xs">⋮⋮</span>
          </button>
          <span className={`text-sm font-mono font-bold ${isDark ? 'text-teal-400' : 'text-teal-700'}`}>{entry.code}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-mono ${isDark ? 'text-cyan-400' : 'text-cyan-700'}`}>{entry.wrvu}</span>
          <button
            onClick={() => onRemove(entry.id)}
            className={`opacity-0 group-hover:opacity-100 transition-all ${
              isDark ? 'text-slate-500 hover:text-red-400' : 'text-gray-400 hover:text-red-600'
            }`}
            title="Remove"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      <div className={`text-sm mt-1 ml-6 break-words ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{entry.description}</div>
      {/* Tooltip for full description - shows on hover if text is long */}
      <div className={`absolute left-0 top-full mt-1 z-[100] px-2 py-1.5 border rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 w-full pointer-events-none ${
        isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-200'
      }`}>
        <p className={`text-sm break-words ${isDark ? 'text-slate-200' : 'text-gray-900'}`}>{entry.description}</p>
        <p className={`text-xs font-mono mt-0.5 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>{entry.code} — {entry.wrvu} wRVU</p>
      </div>
    </div>
  );
}

export default function CptReferencePanel({ onAddToMisc, onAddToProcedure, refreshTrigger }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  // Check if on mobile device
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  });
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Initialize isOpen state - default to closed on mobile, check localStorage on desktop
  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      // Mobile - always start closed
      return false;
    }
    const saved = localStorage.getItem("quickAddCptListOpen");
    // Desktop: if no preference saved, default to open
    return saved === null ? true : saved === "true";
  });
  
  // Initialize panel width from localStorage, default to 256px (16rem)
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem("quickAddCptListWidth");
    return saved ? parseInt(saved, 10) : 256;
  });
  
  const [isResizing, setIsResizing] = useState(false);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newCode, setNewCode] = useState({ code: "", description: "", wrvu: "" });
  const [saving, setSaving] = useState(false);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, entry }
  const [editingEntryId, setEditingEntryId] = useState(null); // ID of entry being edited
  const [editForm, setEditForm] = useState({ code: "", description: "", wrvu: "" });
  const [autoFilled, setAutoFilled] = useState(false); // Track if autofill was applied

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load from backend on mount and when panel opens
  const loadCodes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchUserCptCodes();
      setCodes(data);
    } catch {
      setCodes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadCodes();
    }
  }, [isOpen, loadCodes]);

  // Reload when refreshTrigger changes (e.g., when item added from outside)
  useEffect(() => {
    if (refreshTrigger > 0) {
      loadCodes();
    }
  }, [refreshTrigger, loadCodes]);

  // Save isOpen preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("quickAddCptListOpen", isOpen.toString());
  }, [isOpen]);

  // Save panel width to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("quickAddCptListWidth", panelWidth.toString());
  }, [panelWidth]);

  // Handle resize mouse events
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      // Calculate new width (minimum 200px, maximum 600px)
      const newWidth = Math.max(200, Math.min(600, e.clientX));
      setPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Keyboard shortcut: Press "C" to toggle panel (when not typing in an input)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if user is typing in an input, textarea, or contenteditable
      const target = e.target;
      const isTyping = target.tagName === 'INPUT' || 
                       target.tagName === 'TEXTAREA' || 
                       target.isContentEditable;
      
      if (!isTyping && (e.key === 'c' || e.key === 'C') && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle code input change with autofill
  const handleCodeChange = (value) => {
    setNewCode(prev => ({ ...prev, code: value }));
    setAutoFilled(false);
    
    // Check if the code exists in CPT_LOOKUP for autofill
    const code = value.trim();
    if (code.length === 5 && CPT_LOOKUP[code]) {
      const lookup = CPT_LOOKUP[code];
      setNewCode({
        code: code,
        description: lookup.name,
        wrvu: lookup.wrvu.toString()
      });
      setAutoFilled(true);
    }
  };

  const handleAddCode = async () => {
    if (!newCode.code.trim() || !newCode.description.trim()) return;
    
    setSaving(true);
    try {
      const entry = await addUserCptCode(
        newCode.code.trim(),
        newCode.description.trim(),
        parseFloat(newCode.wrvu) || 0
      );
      setCodes(prev => [...prev, entry]);
      setNewCode({ code: "", description: "", wrvu: "" });
      setIsAdding(false);
    } catch {
      // Silent fail
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveCode = async (id) => {
    try {
      await deleteUserCptCode(id);
      setCodes(prev => prev.filter(c => c.id !== id));
    } catch {
      // Silent fail
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntryId(entry.id);
    setEditForm({ code: entry.code, description: entry.description, wrvu: entry.wrvu.toString() });
    setIsAdding(false); // Close add form if open
  };

  const handleCancelEdit = () => {
    setEditingEntryId(null);
    setEditForm({ code: "", description: "", wrvu: "" });
  };

  const handleSaveEdit = async (entryId) => {
    if (!editForm.code.trim() || !editForm.description.trim()) return;
    
    setSaving(true);
    try {
      const updated = await updateUserCptCode(
        entryId,
        editForm.code.trim(),
        editForm.description.trim(),
        parseFloat(editForm.wrvu) || 0
      );
      setCodes(prev => prev.map(c => c.id === entryId ? updated : c));
      setEditingEntryId(null);
      setEditForm({ code: "", description: "", wrvu: "" });
    } catch {
      // Silent fail
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      setCodes((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        
        // Save new order to backend
        const orderedIds = newOrder.map(item => item.id);
        reorderUserCptCodes(orderedIds).catch(() => {
          // Silent fail - revert on error could be added here
        });
        
        return newOrder;
      });
    }
  };

  const handleContextMenu = (e, entry) => {
    // Show context menu with edit option always available
    setContextMenu({ x: e.clientX, y: e.clientY, entry });
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  return (
    <>
      {/* Toggle Button - Fixed on left side */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{ 
          left: isOpen ? `${panelWidth}px` : '0px'
        }}
        className={`fixed top-1/2 -translate-y-1/2 z-[60] flex items-center gap-0.5 px-1.5 py-3 rounded-r-lg transition-all duration-200 ${
          isOpen 
            ? isDark ? "bg-slate-800/90 text-blue-400" : "bg-white/95 text-blue-600 shadow-md border-r border-y border-gray-200"
            : isDark 
              ? "bg-slate-800/80 hover:bg-slate-700/90 text-slate-400 hover:text-blue-400"
              : "bg-white/90 hover:bg-gray-100 text-gray-500 hover:text-blue-600 shadow-md border-r border-y border-gray-200"
        }`}
        title={isOpen ? "Close Quick-Add CPT List (C)" : "Open Quick-Add CPT List (C)"}
      >
        {isOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        {!isOpen && (
          <div className="flex items-center gap-0.5">
            <span className={`text-[10px] font-mono flex items-center justify-center ${isDark ? 'text-slate-500' : 'text-gray-400'}`} style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}>
              Press C
            </span>
            <span className="text-xs font-semibold" style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}>
              Quick-Add CPT List
            </span>
          </div>
        )}
      </button>

      {/* Sidebar Panel */}
      <div
        style={{ width: `${panelWidth}px` }}
        className={`fixed left-0 top-0 h-full backdrop-blur-sm border-r z-[55] transition-transform duration-200 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } ${
          isDark 
            ? 'bg-slate-900/95 border-slate-700/50'
            : 'bg-white/98 border-gray-200 shadow-xl'
        }`}
      >
        <div className="flex flex-col h-full pb-20">
          {/* Header */}
          <div className={`p-4 border-b ${isDark ? 'border-slate-700/50' : 'border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BookOpen size={20} className="text-blue-400" />
                <h3 className={`text-base font-bold ${isDark ? 'text-slate-200' : 'text-gray-900'}`}>Quick-Add CPT List</h3>
              </div>
              <kbd className={`px-1.5 py-0.5 text-[10px] font-mono rounded border ${
                isDark ? 'bg-slate-800 border-slate-600 text-slate-400' : 'bg-gray-100 border-gray-300 text-gray-500'
              }`}>C</kbd>
            </div>
            <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
              <span className="hidden md:inline">Right-click to add • Drag to reorder</span>
              <span className="md:hidden">Long-press to add • Drag to reorder</span>
            </p>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-3">
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin h-5 w-5 border-2 border-blue-400 border-t-transparent rounded-full" />
              </div>
            ) : codes.length === 0 && !isAdding ? (
              <div className="text-center py-6">
                <BookOpen size={32} className={`mx-auto mb-2 ${isDark ? 'text-slate-600' : 'text-gray-400'}`} />
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>No codes added yet</p>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>Add your commonly used CPT codes</p>
              </div>
            ) : null}

            {/* Code List - Sortable */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={codes.map(c => c.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {codes.map((entry) => (
                    <SortableItem
                      key={entry.id}
                      entry={entry}
                      onRemove={handleRemoveCode}
                      onContextMenu={handleContextMenu}
                      isEditing={editingEntryId === entry.id}
                      onEdit={handleEditEntry}
                      onSaveEdit={() => handleSaveEdit(entry.id)}
                      onCancelEdit={handleCancelEdit}
                      editForm={editForm}
                      onEditFormChange={setEditForm}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {/* Add Form - styled like existing entries */}
            {isAdding && (
              <div className={`mt-2 p-2 rounded-lg border ring-1 ${
                isDark 
                  ? 'bg-slate-800/60 border-blue-500/50 ring-blue-500/30'
                  : 'bg-blue-50 border-blue-300 ring-blue-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>⋮⋮</span>
                    <Input
                      placeholder="Code"
                      value={newCode.code}
                      onChange={(e) => handleCodeChange(e.target.value)}
                      className={`bg-transparent border-none text-sm font-mono font-bold h-6 w-14 p-0 focus-visible:ring-0 ${
                        isDark ? 'text-teal-400 placeholder:text-teal-400/40' : 'text-teal-700 placeholder:text-teal-700/40'
                      }`}
                      maxLength={5}
                      autoFocus
                    />
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="wRVU"
                    value={newCode.wrvu}
                    onChange={(e) => setNewCode(prev => ({ ...prev, wrvu: e.target.value }))}
                    className={`bg-transparent border-none text-sm font-mono h-6 w-12 p-0 text-right focus-visible:ring-0 ${
                      isDark ? 'text-cyan-400 placeholder:text-cyan-400/40' : 'text-cyan-700 placeholder:text-cyan-700/40'
                    }`}
                  />
                </div>
                <Input
                  placeholder="Description"
                  value={newCode.description}
                  onChange={(e) => setNewCode(prev => ({ ...prev, description: e.target.value }))}
                  className={`bg-transparent border-none text-xs h-5 mt-1 ml-6 p-0 focus-visible:ring-0 ${
                    isDark ? 'text-slate-400 placeholder:text-slate-500' : 'text-gray-700 placeholder:text-gray-400'
                  }`}
                />
                {autoFilled && (
                  <div className="flex items-center gap-1.5 mt-1 ml-6">
                    <Zap size={10} className="text-emerald-400" />
                    <span className="text-[10px] text-emerald-400">Auto-filled</span>
                  </div>
                )}
                <div className={`flex items-center gap-2 mt-2 pt-2 border-t ${isDark ? 'border-slate-700/50' : 'border-gray-200'}`}>
                  <Button
                    onClick={handleAddCode}
                    disabled={!newCode.code.trim() || !newCode.description.trim() || saving}
                    size="sm"
                    className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-6"
                  >
                    {saving ? "..." : "Add"}
                  </Button>
                  <Button
                    onClick={() => { setIsAdding(false); setNewCode({ code: "", description: "", wrvu: "" }); setAutoFilled(false); }}
                    size="sm"
                    variant="ghost"
                    className={`text-xs h-6 px-2 ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Footer - Right-click reminder and Add Button */}
          <div className={`border-t ${isDark ? 'border-slate-700/50' : 'border-gray-200'}`}>
            {/* Right-click reminder - always visible */}
            {codes.length > 0 && (
              <div className="px-3 pt-3">
                <div className={`p-2 rounded-lg border ${
                  isDark 
                    ? 'bg-yellow-500/10 border-yellow-500/30'
                    : 'bg-yellow-50 border-yellow-200'
                }`}>
                  <p className={`text-xs text-center flex items-center justify-center gap-1 ${
                    isDark ? 'text-yellow-400' : 'text-yellow-700'
                  }`}>
                    <span>💡</span>
                    <span className="hidden md:inline">Right-click to add to Tracker</span>
                    <span className="md:hidden">Long-press to add to Tracker</span>
                  </p>
                </div>
              </div>
            )}
            
            {!isAdding && (
              <div className="p-3">
                <Button
                  onClick={() => setIsAdding(true)}
                  className="w-full bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-8"
                >
                  <Plus size={14} className="mr-1" />
                  Add CPT Code
                </Button>
              </div>
            )}
          </div>
        </div>
        
        {/* Resize Handle */}
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            setIsResizing(true);
          }}
          className={`absolute top-0 right-0 w-1 h-full cursor-ew-resize hover:bg-blue-500/50 active:bg-blue-500 transition-colors group`}
          title="Drag to resize"
        >
          <div className={`absolute top-1/2 -translate-y-1/2 right-0 w-1 h-12 rounded-l group-hover:bg-blue-500/70 transition-colors ${
            isDark ? 'bg-slate-600/50' : 'bg-gray-300/50'
          }`} />
        </div>
      </div>

      {/* Overlay when open on mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-10 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          entry={contextMenu.entry}
          onClose={closeContextMenu}
          onAddToMisc={onAddToMisc}
          onAddToProcedure={onAddToProcedure}
          onEdit={handleEditEntry}
        />
      )}
    </>
  );
}
